import { types as t, getRoot, cast } from 'mobx-state-tree'
import globalAllVariablesWide from '../../../datasets/global-SCATTERPLOT-simple-gdp-imputed.csv'
import { uniq, uniqBy, pickBy, cloneDeep, isEqual } from 'lodash-es'
import { extent } from 'd3-array'
import {
  Datum,
  GeoFilter,
  ChallengesMap,
  ChallengeInfo,
  ChallengeColumnName,
  ScaleType,
  Path,
  DatumWithCoords,
  NumericDomain,
  NumericRange,
} from '../types'
import { SCALES, CHALLENGES, GEO_FILTER, MAX_ACTIVE_COUNTRIES } from '../constants'
import { isBetween } from '../../../lib/math-utils'
import { expandDirection, expandRange } from '../utils'

const dataset = (globalAllVariablesWide as Datum[]).sort((a, b) => {
  if (a.country_code === 'USA') return -1
  if (b.country_code === 'USA') return 1
  return a.country < b.country ? -1 : 1
})
const allChallengesNames = Object.keys(CHALLENGES)

export const ScatterplotModel = t
  .model('ScatterplotModel', {
    isPerCapitaActive: true,
    areSymlogScalesActive: true,
    selectedYear: 1990,
    challenges: t.frozen(CHALLENGES),
    geoFilters: t.maybeNull(t.frozen()),
    overedCountry: '',
    activeCountries: t.array(t.string),
    isXAxisDragging: false,
    isYAxisDragging: false,
    xDomainMinOverride: t.maybeNull(t.number),
    xDomainMaxOverride: t.maybeNull(t.number),
    yDomainMinOverride: t.maybeNull(t.number),
    yDomainMaxOverride: t.maybeNull(t.number),
    isMobileModalOpened: false,
    showXYLabels: true,
    debug: false,
  })
  .views((self) => ({
    get root() {
      return getRoot(self)
    },

    get scaleType() {
      return self.areSymlogScalesActive ? ScaleType.SYMLOG : ScaleType.LINEAR
    },

    get xUnitOfMeasure() {
      return '$'
    },

    get yUnitOfMeasure() {
      return self.isPerCapitaActive ? 'mt CO₂' : 'mmt CO₂'
    },
  }))
  .views((self) => ({
    get scaleFunction() {
      return SCALES[self.scaleType].function
    },

    get scaleAriosity() {
      return SCALES[self.scaleType].ariosity
    },

    get yAxisName() {
      return `TOTAL EMISSIONS [${self.yUnitOfMeasure}]`
    },

    get xAxisName() {
      return `GDP [${self.xUnitOfMeasure}]`
    },

    get isThereACountryOvered() {
      return self.overedCountry !== ''
    },

    isCountryOvered(country: string) {
      return self.overedCountry === country
    },

    isCountryActive(country: string) {
      return self.activeCountries.includes(country)
    },

    get yearsExtent(): [number, number] {
      const years = uniq(dataset.map((d) => d.year))
      return extent(years)
    },

    get activeChallenges(): Partial<ChallengesMap<ChallengeInfo>> {
      return pickBy(self.challenges, ['isActive', true])
    },

    get activeGeoFilters(): GeoFilter[] {
      return (self.geoFilters as GeoFilter[]).filter((d) => d.isActive === true)
    },

    get notActiveGeoFilters(): GeoFilter[] {
      return (self.geoFilters as GeoFilter[]).filter((d) => d.isActive === false)
    },

    get geoMapping(): Array<{
      country: string
      region_name: string
      sub_region_name: string
    }> {
      return uniqBy(
        dataset.map((d) => ({
          country: d.country,
          region_name: d.region_name,
          sub_region_name: d.sub_region_name,
        })),
        'country'
      )
    },
  }))
  .views((self) => ({
    get xScaleFn(): any {
      return self.scaleFunction
    },

    get yScaleFn(): any {
      return self.scaleFunction
    },

    get countryOptions() {
      return self.geoMapping.map((o) => ({ value: o.country, label: o.country }))
    },

    get activeCountriesWithLabel() {
      return self.activeCountries.map((c) => ({ value: c, label: c }))
    },

    emissionTotalAbsoluteByChallenges(
      d: Datum | undefined,
      challenges: string[] = allChallengesNames
    ): number {
      let sum = 0
      challenges.forEach((challenge) => {
        const emissionValue = Number(d[challenge]) || 0
        return (sum = sum + emissionValue)
      })
      return sum
    },

    gdpAbsolute(d: Datum | undefined): number {
      if (d === undefined) return 0
      return Number(d.gdp) || 0
    },

    isColumnImputed(d: Datum, columnName: string) {
      const columnNameImputed = `${columnName}_imputed`
      return Boolean(d[columnNameImputed])
    },
  }))
  .views((self) => ({
    emissionTotalPerCapita(
      d: Datum | undefined,
      challenges: string[] = allChallengesNames
    ): number {
      const emissionTotal = self.emissionTotalAbsoluteByChallenges(d, challenges)
      return self.isPerCapitaActive ? (emissionTotal / d.pop_total) * 1e6 : emissionTotal
    },

    gdpPerCapita(d: Datum | undefined) {
      return self.gdpAbsolute(d) / d.pop_total
    },
  }))
  .views((self) => ({
    emissionTotal(d: Datum | undefined, challenges: string[] = allChallengesNames): number {
      return self.isPerCapitaActive
        ? self.emissionTotalPerCapita(d, challenges)
        : self.emissionTotalAbsoluteByChallenges(d, challenges)
    },

    gdp(d: Datum | undefined) {
      return self.isPerCapitaActive ? self.gdpPerCapita(d) : self.gdpAbsolute(d)
    },
  }))
  .views((self) => ({
    get xDomainGreater(): NumericDomain {
      const gdps = dataset.map((d) => self.gdp(d))
      const domain = extent(gdps)
      const extents = { min: Math.min(domain[0], 0), max: domain[1] }
      const ariosity = self.scaleAriosity.x
      const tmpRange = [0, 1] as NumericRange
      const xRangeBigger = expandRange(tmpRange, [ariosity.min, ariosity.max])
      const tmpScale = self.scaleFunction().domain([extents.min, extents.max]).range(tmpRange)
      const xDomainBigger = xRangeBigger.map((d) => tmpScale.invert(d))
      if (xDomainBigger[0] < 0) xDomainBigger[0] = 0
      tmpScale.domain(xDomainBigger)
      return tmpScale.domain()
    },

    get yDomainGreater(): NumericDomain {
      const emissionTotals = dataset.map((d) => self.emissionTotal(d))
      const domain = extent(emissionTotals)
      const extents = { min: domain[0], max: domain[1] }
      const ariosity = self.scaleAriosity.y
      const tmpRange = [0, 1] as NumericRange
      const yRangeBigger = expandRange(tmpRange, [ariosity.min, ariosity.max])
      const tmpScale = self.scaleFunction().domain([extents.min, extents.max]).range(tmpRange)
      const yDomainBigger = yRangeBigger.map((d) => tmpScale.invert(d))
      tmpScale.domain(yDomainBigger)
      return tmpScale.domain()
    },
  }))
  .views((self) => ({
    get xDomain(): NumericDomain {
      const min = self.xDomainMinOverride || self.xDomainGreater[0]
      const max = self.xDomainMaxOverride || self.xDomainGreater[1]
      return [min, max]
    },

    get yDomain(): NumericDomain {
      const min = self.yDomainMinOverride || self.yDomainGreater[0]
      const max = self.yDomainMaxOverride || self.yDomainGreater[1]
      return [min, max]
    },
  }))
  .views((self) => ({
    isIntoDomains(x: number, y: number) {
      return isBetween(self.xDomain, x) && isBetween(self.yDomain, y)
    },
  }))
  .views((self) => ({
    get circles(): Array<DatumWithCoords & { showXNA: boolean; showYNA: boolean }> {
      const activeChallengesNames = Object.keys(self.activeChallenges)
      const activeGeoFiltersNames = self.activeGeoFilters.map((sr) => sr.name)
      // visibile countries can be:
      // - filtered countries by clicking datum point or using multiselect
      // - countries by active regions
      const datasetBySelectedYearAndGeoFilters = dataset.filter(
        (d) =>
          d.year === self.selectedYear &&
          (activeGeoFiltersNames.includes(d[GEO_FILTER.columnName]) ||
            self.activeCountries.includes(d.country))
      )
      const points = datasetBySelectedYearAndGeoFilters.map((datum) => {
        const x = self.gdp(datum)
        const y = self.emissionTotal(datum, activeChallengesNames)
        const showXNA = self.isColumnImputed(datum, 'gdp')
        const showYNA =
          activeChallengesNames.length === 1 &&
          self.isColumnImputed(datum, activeChallengesNames[0])
        return { x, y, showXNA, showYNA, ...datum }
      })
      return points
    },

    get activeOrOveredCountries(): string[] {
      const existsAnOveredCountry = self.overedCountry.length > 0
      return existsAnOveredCountry
        ? uniq([...self.activeCountries, self.overedCountry])
        : self.activeCountries
    },

    pathPoints(country: string): DatumWithCoords[] {
      const activeChallengesNames = Object.keys(self.activeChallenges)
      const datasetFilteredBySelectedCountry = dataset.filter((d) => d.country === country)
      const points = datasetFilteredBySelectedCountry.map((d) => {
        return {
          x: self.gdp(d),
          y: self.emissionTotal(d, activeChallengesNames),
          ...d,
        }
      })
      return points
    },
  }))
  .views((self) => ({
    get paths(): Path[] {
      return self.activeOrOveredCountries.map((country) => ({
        country,
        pathPoints: self.pathPoints(country),
      }))
    },
  }))
  .actions((self) => ({
    setIsMobileModalOpened(value: boolean) {
      self.isMobileModalOpened = value
    },

    setXDomainMinOverride(value: number) {
      self.xDomainMinOverride = value
    },

    resetXDomainMinOverride() {
      self.xDomainMinOverride = null
    },

    setXDomainMaxOverride(value: number) {
      self.xDomainMaxOverride = value
    },

    resetXDomainMaxOverride() {
      self.xDomainMaxOverride = null
    },

    setYDomainMinOverride(value: number) {
      self.yDomainMinOverride = value
    },

    resetYDomainMinOverride() {
      self.yDomainMinOverride = null
    },

    setYDomainMaxOverride(value: number) {
      self.yDomainMaxOverride = value
    },

    resetYDomainMaxOverride() {
      self.yDomainMaxOverride = null
    },

    setChallenges(challenges: ChallengesMap<ChallengeInfo>) {
      self.challenges = challenges
    },

    setIsXAxisDragging(value: boolean) {
      self.isXAxisDragging = value
    },

    setIsYAxisDragging(value: boolean) {
      self.isYAxisDragging = value
    },

    setActiveCountries(countries: string[]) {
      self.activeCountries = cast(countries)
    },

    resetActiveCountries() {
      self.activeCountries = cast([])
    },

    toggleShowXYLabels() {
      self.showXYLabels = !self.showXYLabels
    },
  }))
  .actions((self) => ({
    addActiveCountry(country: string) {
      if (!country) return
      const newActiveCountries = uniq([...self.activeCountries, country])
      if (self.activeCountries.length >= MAX_ACTIVE_COUNTRIES) return
      self.setActiveCountries(newActiveCountries)
    },

    removeActiveCountry(country: string) {
      if (!country) return
      const newActiveCountries = [...self.activeCountries].filter((d) => d !== country)
      self.setActiveCountries(newActiveCountries)
    },

    setIsPerCapitaActive(value: boolean) {
      // reset smaller domains (the ones user can play with)
      self.resetXDomainMinOverride()
      self.resetXDomainMaxOverride()
      self.resetYDomainMinOverride()
      self.resetYDomainMaxOverride()
      // recompute domains
      self.xDomain
      self.yDomain
      // update per capita value
      self.isPerCapitaActive = value
    },

    toggleIsPerCapitaActive() {
      // reset smaller domains (the ones user can play with)
      self.resetXDomainMinOverride()
      self.resetXDomainMaxOverride()
      self.resetYDomainMinOverride()
      self.resetYDomainMaxOverride()
      // recompute domains
      self.xDomain
      self.yDomain
      // update per capita value
      self.isPerCapitaActive = !self.isPerCapitaActive
    },

    setAreSymlogScalesActive(value: boolean) {
      self.areSymlogScalesActive = value
    },

    toggleAreSymlogScalesActive() {
      // reset smaller domains (the ones user can play with)
      self.resetXDomainMinOverride()
      self.resetXDomainMaxOverride()
      self.resetYDomainMinOverride()
      self.resetYDomainMaxOverride()
      // update symlog/linear value
      self.areSymlogScalesActive = !self.areSymlogScalesActive
    },

    setSelectedYear(year: number) {
      self.selectedYear = year
    },

    setGeoFilters(geoFilters: GeoFilter[]) {
      self.geoFilters = geoFilters
    },

    setOveredCountry(country: string) {
      self.overedCountry = country
    },

    resetOveredCountry() {
      self.overedCountry = ''
    },

    setDebug(value: boolean) {
      self.debug = value
    },

    toggleDebug() {
      self.debug = !self.debug
    },
  }))
  .actions((self) => ({
    resetGeoFilters() {
      const geoFilterNames = uniq(dataset.map((d) => d[GEO_FILTER.columnName]))
      self.setGeoFilters(
        geoFilterNames.map((s) => ({
          name: s,
          isActive: true,
        }))
      )
    },

    setChallengeValue(challengeColumnName: ChallengeColumnName, value: number) {
      const newChallenges = cloneDeep(self.challenges)
      newChallenges[challengeColumnName].value = value
      self.challenges = newChallenges
    },

    toggleChallenge(challengeColumnName: ChallengeColumnName) {
      const newChallenges = cloneDeep(self.challenges)
      newChallenges[challengeColumnName].isActive = !self.challenges[challengeColumnName].isActive
      self.challenges = newChallenges
    },

    toggleGeoFilter(geoFilter: string) {
      const newSubregions = (self.geoFilters as GeoFilter[]).map((sr) =>
        sr.name === geoFilter ? { ...sr, isActive: !sr.isActive } : sr
      )
      self.setGeoFilters(newSubregions)
    },

    toggleActiveCountry(country: string) {
      if (!country) return
      const activeCountries = [...self.activeCountries] // clone
      if (activeCountries.includes(country)) {
        // remove that country to the active countries
        self.removeActiveCountry(country)
      } else {
        // add that country to the active countries
        self.addActiveCountry(country)
      }
    },
  }))
  .actions((self) => ({
    updateDomainAfterFiltering() {
      const { circles, activeCountries, xDomain, yDomain, scaleAriosity } = self
      // extend domains by including the filtered countries
      const selectedCountries = circles.filter((p) => activeCountries.includes(p.country))
      const xs = [...selectedCountries.map((c) => c.x), ...xDomain]
      const ys = [...selectedCountries.map((c) => c.y), ...yDomain]
      const newXDom = extent(xs)
      const newYDom = extent(ys)
      const hasToExpandX = !isEqual(xDomain, newXDom)
      const hasToExpandY = !isEqual(yDomain, newYDom)
      const xDirection = expandDirection(xDomain, newXDom)
      const yDirection = expandDirection(yDomain, newYDom)
      const tmpRange = [0, 1] as NumericRange

      if (hasToExpandX) {
        const xAriosity = self.scaleAriosity.x
        const xRangeBigger = expandRange(tmpRange, [xAriosity.min, xAriosity.max], xDirection)
        const tmpXScale = self.scaleFunction().domain(newXDom).range(tmpRange)
        const xDomainBigger = xRangeBigger.map((d) => tmpXScale.invert(d))
        if (xDomainBigger[0] < 0) xDomainBigger[0] = 0
        tmpXScale.domain(xDomainBigger)
        self.setXDomainMinOverride(xDomainBigger[0])
        self.setXDomainMaxOverride(xDomainBigger[1])
      }
      if (hasToExpandY) {
        const yAriosity = self.scaleAriosity.y
        const yRangeBigger = expandRange(tmpRange, [yAriosity.min, yAriosity.max], yDirection)
        const tmpYScale = self.scaleFunction().domain(newYDom).range(tmpRange)
        const yDomainBigger = yRangeBigger.map((d) => tmpYScale.invert(d))
        tmpYScale.domain(yDomainBigger)
        self.setYDomainMinOverride(yDomainBigger[0])
        self.setYDomainMaxOverride(yDomainBigger[1])
      }
    },

    setChallengesValues(datum: Datum) {
      Object.keys(self.challenges).forEach((challangeColumnName) =>
        self.setChallengeValue(
          challangeColumnName as ChallengeColumnName,
          datum[challangeColumnName]
        )
      )
    },
  }))
  .actions((self) => ({
    resetModalFilters() {
      self.resetActiveCountries()
      self.resetGeoFilters()
      self.setIsPerCapitaActive(true)
      self.setAreSymlogScalesActive(true)
    },
  }))
  .actions((self) => ({
    afterCreate() {
      self.resetGeoFilters()
    },
  }))
